import {
  Button,
  Card,
  Checkbox,
  Input,
  InputNumber,
  Space,
  Calendar,
  Select,
  Spin,
  Alert,
  Row,
  Col,
  DatePicker,
  Tooltip,
  notification,
} from "antd";
import {
  LeftOutlined,
  RightOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  WarningFilled,
} from "@ant-design/icons";
import dayjs from "dayjs";
import "dayjs/locale/lt";
import locale from "antd/es/date-picker/locale/lt_LT";
import type { Dayjs } from "dayjs";
import { useFormBase, City } from "../shared/useFormBase";
import { isPlausibleBirthDate, birthDatePickerDefault } from "../shared/birthDate";
import StepIndicator from "../shared/StepIndicator";

import CardPartial from "../../partials/Card";
import type { FormConfig } from "../../App";

interface PrivatePartyFormProps {
  onSubmitSuccess: () => void;
  reservationType: string | null;
  formConfig: FormConfig;
}

const PrivatePartyForm: React.FC<PrivatePartyFormProps> = ({
  onSubmitSuccess,
  reservationType,
  formConfig,
}) => {
  const form = useFormBase({
    reservationType,
    formConfig,
    onSubmitSuccess,
    formTemplate: "private_party",
  });

  const totalSteps = 4;
  const stepLabels = ["Data ir laikas", "Kontaktai", "Papildukai", "Santrauka"];

  const canGoNext = () => {
    switch (form.currentStep) {
      case 0:
        return !!form.selectedCity && !!form.selectedDate && !!form.activeTime;
      case 1:
        return (
          form.parentName.length >= 3 &&
          form.phone.length === 8 &&
          form.customerEmail.length > 0 &&
          form.address.length >= 5 &&
          form.guestCount >= 1
        );
      case 2:
        return true; // Services optional
      case 3:
        return form.confirmationChecked && form.dataProcessingConsent;
      default:
        return false;
    }
  };

  const handleSubmit = () => {
    form.submitOrder({
      print_invites: false,
      phone_in_invitation: false,
    });
  };

  const calendarFilter = (current: Dayjs) => {
    return current && current < dayjs().startOf("day");
  };

  const handleCalendarChange = (date: Dayjs) => {
    form.handleDateChange(date.format("YYYY-MM-DD"));
  };

  // Filter only evening slots (19:00+) for private parties
  const eveningTimes = form.availableTimes.filter((t: any) => {
    const hour = parseInt(t.time_start?.split(":")[0] || "0");
    return hour >= 19;
  });

  const handleTimeSelect = (timeUuid: string) => {
    form.setActiveTime(timeUuid);

    // Check the time slot's own extension properties (configured in TVS)
    const selectedTime = eveningTimes.find(
      (time: any) => time.uuid === timeUuid,
    );
    const extensionsEnabled =
      selectedTime?.allow_start_adjustment ||
      selectedTime?.allow_end_adjustment ||
      false;
    form.setIsExtensionEnabled(extensionsEnabled);
    // If start adjustment allowed (time moves earlier), show warning about earlier start
    form.setIsFirstTimeSlotSelected(
      selectedTime?.allow_start_adjustment &&
        !selectedTime?.allow_end_adjustment,
    );

    // Remove extension services that are disabled or exceed max extension for this time slot
    const maxMins = Math.max(
      selectedTime?.allow_start_adjustment
        ? selectedTime?.start_adjustment_minutes || 0
        : 0,
      selectedTime?.allow_end_adjustment
        ? selectedTime?.end_adjustment_minutes || 0
        : 0,
    );
    const invalidExtensionUuids = form.services
      .filter((s: any) => {
        if (!s.extension_minutes) return false;
        if (!extensionsEnabled) return true;
        return s.extension_minutes > maxMins;
      })
      .map((s: any) => s.uuid);
    if (invalidExtensionUuids.length > 0) {
      const cleaned = form.selectedServices.filter(
        (uuid: string) => !invalidExtensionUuids.includes(uuid),
      );
      if (cleaned.length !== form.selectedServices.length) {
        form.setSelectedServices(cleaned);
      }
    }
  };

  const renderStep = () => {
    switch (form.currentStep) {
      case 0:
        return (
          <>
            <CardPartial isPosting={form.isPosting} active_time={"none"}>
              <div style={{ textAlign: "center" }}>
                <Space>
                  {form.cities.map((city: City) => (
                    <Button
                      type={
                        form.selectedCity?.uuid === city.uuid
                          ? "primary"
                          : "default"
                      }
                      key={city.uuid}
                      onClick={() => form.handleCityChange(city)}
                    >
                      {city.name}
                    </Button>
                  ))}
                </Space>
              </div>
            </CardPartial>

            {form.selectedCity ? (
              <>
                <CardPartial isPosting={form.isPosting} active_time={"none"}>
                  <div style={{ textAlign: "center" }}>
                    Pasirinktas miestas:{" "}
                    <span style={{ fontWeight: "bold" }}>
                      {form.selectedCity.name}
                    </span>
                  </div>
                </CardPartial>

                <CardPartial isPosting={form.isPosting} active_time={"none"}>
                  <Spin tip="Kraunasi" size="large" spinning={form.loading}>
                    <Calendar
                      onSelect={handleCalendarChange}
                      defaultValue={dayjs().add(1, "day")}
                      disabledDate={calendarFilter}
                      locale={locale}
                      headerRender={({ value, onChange }) => {
                        const months: string[] = [];
                        for (let i = 0; i < 12; i++) {
                          const m = value.clone().month(i);
                          const name = m.localeData().months(m).toString();
                          months.push(
                            name.charAt(0).toUpperCase() + name.slice(1),
                          );
                        }

                        return (
                          <div style={{ padding: 8 }}>
                            <Row
                              gutter={8}
                              justify="space-between"
                              align="middle"
                            >
                              <Col>
                                <Button
                                  size="small"
                                  onClick={() =>
                                    onChange(value.clone().subtract(1, "month"))
                                  }
                                  icon={<LeftOutlined />}
                                />
                              </Col>
                              <Col>
                                <Space size={4}>
                                  <Select
                                    size="small"
                                    value={value.year()}
                                    style={{ minWidth: "80px" }}
                                    onChange={(y) =>
                                      onChange(value.clone().year(y))
                                    }
                                  >
                                    {Array.from(
                                      { length: 20 },
                                      (_, i) => value.year() - 10 + i,
                                    ).map((y) => (
                                      <Select.Option key={y} value={y}>
                                        {y}
                                      </Select.Option>
                                    ))}
                                  </Select>
                                  <Select
                                    size="small"
                                    value={value.month()}
                                    style={{ minWidth: "100px" }}
                                    onChange={(m) =>
                                      onChange(value.clone().month(m))
                                    }
                                  >
                                    {months.map((m, i) => (
                                      <Select.Option key={i} value={i}>
                                        {m}
                                      </Select.Option>
                                    ))}
                                  </Select>
                                </Space>
                              </Col>
                              <Col>
                                <Button
                                  size="small"
                                  onClick={() =>
                                    onChange(value.clone().add(1, "month"))
                                  }
                                  icon={<RightOutlined />}
                                />
                              </Col>
                            </Row>
                          </div>
                        );
                      }}
                      validRange={[dayjs(), dayjs().add(30, "month")]}
                      fullscreen={false}
                    />
                  </Spin>
                </CardPartial>
              </>
            ) : (
              <Alert
                type="warning"
                message="Pirma pasirinkite miestą, kad matytumėte kalendorių"
                banner
              />
            )}

            <CardPartial
              isPosting={form.isPosting}
              title="Laisvi laikai"
              active_time={"none"}
            >
              <Space
                style={{ width: "100%", justifyContent: "center" }}
                wrap
                direction="horizontal"
              >
                {!form.selectedCity ? (
                  <Alert
                    type="warning"
                    message="Pirma pasirinkite miestą, kad matytumėte laisvus laikus"
                    banner
                  />
                ) : eveningTimes.length <= 0 ? (
                  <Alert
                    type="info"
                    message="Jūsų pasirinktai datai nėra laisvų vakarinių laikų, pasirinkite kitą datą"
                    banner
                  />
                ) : (
                  eveningTimes.map((time: any) => (
                    <Button
                      type={
                        form.activeTime === time.uuid ? "primary" : "default"
                      }
                      key={time.uuid}
                      onClick={() => handleTimeSelect(time.uuid)}
                      size="small"
                    >
                      {time.time_start.substring(0, time.time_start.length - 3)}
                      -{time.time_end.substring(0, time.time_end.length - 3)}
                    </Button>
                  ))
                )}
              </Space>
            </CardPartial>
          </>
        );

      case 1:
        return (
          <CardPartial
            isPosting={form.isPosting}
            title="Kontaktinė informacija"
            active_time={form.activeTime}
          >
            <Space className="w-full" direction="vertical" wrap>
              <label>Jubiliato vardas</label>
              <Input
                value={form.childName}
                onChange={(e) => form.setChildName(e.target.value)}
                placeholder="Jonas"
              />
              <small style={{ fontSize: 11, color: "#999" }}>
                Įrašykite Jubiliato vardą jei švenčiamas gimtadienis
              </small>

              <label>Jubiliato gimimo data, jei švenčiamas gimtadienis</label>
              <DatePicker
                value={form.childBirthDate ? dayjs(form.childBirthDate) : null}
                onChange={(date) =>
                  form.setChildBirthDate(date ? date.format("YYYY-MM-DD") : "")
                }
                placeholder="Pasirinkite gimimo datą pvz. 1986-07-30"
                format="YYYY-MM-DD"
                style={{ width: "100%" }}
                allowClear={false}
                disabledDate={(date) => !isPlausibleBirthDate(date)}
                defaultPickerValue={birthDatePickerDefault()}
              />
              {(() => {
                if (!form.childBirthDate || !form.selectedDate) return null;
                const birth = dayjs(form.childBirthDate);
                const party = dayjs(form.selectedDate);
                if (!birth.isValid() || !party.isValid()) return null;
                const birthdayThisYear = birth.year(party.year());
                const birthdayNextYear = birth.year(party.year() + 1);
                const birthdayLastYear = birth.year(party.year() - 1);
                const diffThis = Math.abs(party.diff(birthdayThisYear, "day"));
                const diffNext = Math.abs(party.diff(birthdayNextYear, "day"));
                const diffLast = Math.abs(party.diff(birthdayLastYear, "day"));
                let age = 0;
                if (diffThis <= diffLast && diffThis <= diffNext) {
                  age = party.year() - birth.year();
                } else if (diffLast <= diffNext) {
                  age = party.year() - 1 - birth.year();
                } else {
                  age = party.year() + 1 - birth.year();
                }
                if (age <= 0) return null;
                return (
                  <div
                    style={{
                      textAlign: "center",
                      color: "#52c41a",
                      fontWeight: 600,
                      fontSize: 14,
                      marginTop: 4,
                    }}
                  >
                    Bus švenčiamas {age}-asis gimtadienis! 🎉
                  </div>
                );
              })()}

              <label>Planuojamas svečių skaičius</label>
              <InputNumber
                min={1}
                max={100}
                value={form.guestCount}
                onChange={(val) => form.setGuestCount(val || 1)}
                style={{ width: "100%" }}
              />

              <label>Užsakovo vardas ir pavardė</label>
              <Input
                value={form.parentName}
                onChange={(e) => form.setParentName(e.target.value)}
                placeholder="Vardas Pavardė"
                autoComplete="name"
              />

              <label>Gyvenamosios vietos adresas</label>
              <Input
                value={form.address}
                onChange={(e) => form.setAddress(e.target.value)}
                placeholder="Gatvė, miestas"
                autoComplete="street-address"
              />

              <label>Telefono numeris</label>
              <Input
                addonBefore="+370"
                value={form.phone}
                onChange={(e) => {
                  let value = e.target.value;
                  value = value.replace(/\D/g, "");
                  if (value.startsWith("00370")) {
                    value = value.substring(5);
                  } else if (value.startsWith("370")) {
                    value = value.substring(3);
                  }
                  if (
                    (value.startsWith("0") || value.startsWith("8")) &&
                    value.length > 8
                  ) {
                    value = value.substring(1);
                  }
                  value = value.substring(0, 8);
                  form.setPhone(value);
                }}
                placeholder="66612345"
                autoComplete="tel-national"
              />

              <label>El. paštas</label>
              <Input
                type="email"
                value={form.customerEmail}
                onChange={(e) => form.setCustomerEmail(e.target.value)}
                placeholder="el@pastas.lt"
                autoComplete="email"
              />
            </Space>
          </CardPartial>
        );

      case 2: {
        // Get selected time slot to check max extension minutes
        const selectedTimeSlot = eveningTimes.find(
          (t: any) => t.uuid === form.activeTime,
        );
        const maxExtensionMinutes = Math.max(
          selectedTimeSlot?.allow_start_adjustment
            ? selectedTimeSlot?.start_adjustment_minutes || 0
            : 0,
          selectedTimeSlot?.allow_end_adjustment
            ? selectedTimeSlot?.end_adjustment_minutes || 0
            : 0,
        );

        // Identify extension services by extension_minutes field
        const extensionServiceUuids = form.services
          .filter((s: any) => s.extension_minutes)
          .map((s: any) => s.uuid);
        const hasExtensionSelected = form.selectedServices.some(
          (uuid: string) => extensionServiceUuids.includes(uuid),
        );

        return (
          <CardPartial
            isPosting={form.isPosting}
            title="Papildomos paslaugos"
            active_time={form.activeTime}
          >
            <Space
              className="w-full"
              direction="vertical"
              style={{ width: "100%" }}
            >
              {form.isFirstTimeSlotSelected &&
                form.isExtensionEnabled &&
                hasExtensionSelected && (
                  <Alert
                    message="Vakarėlio laikas ankstinamas"
                    description="Pasirinkus pratęsimą, vakarėlio pradžios laikas bus paankstintas, o ne pavėlintas."
                    type="warning"
                    showIcon
                    icon={<WarningFilled />}
                    style={{ marginBottom: 8 }}
                  />
                )}
              {form.services.length === 0 ? (
                <Alert type="info" message="Papildomų paslaugų nėra" banner />
              ) : (
                [...form.services]
                  .sort((a: any, b: any) => a.price - b.price)
                  .map((svc: any) => {
                    const isExtensionService = !!svc.extension_minutes;
                    const exceedsMax =
                      isExtensionService &&
                      svc.extension_minutes > maxExtensionMinutes;
                    const isExtensionDisabled =
                      isExtensionService &&
                      (!form.isExtensionEnabled || exceedsMax);

                    const tooltipText = !form.isExtensionEnabled
                      ? "Pratęsimas neprieinamas šiam laikui!"
                      : `Šiam laikui galimas pratęsimas iki ${Math.floor(maxExtensionMinutes / 60)} val.`;

                    return (
                      <Row key={svc.uuid} align="middle">
                        <Col>
                          <Checkbox
                            checked={form.selectedServices.includes(svc.uuid)}
                            onChange={() => {
                              if (!isExtensionDisabled) {
                                form.toggleService(svc.uuid);
                              }
                            }}
                            disabled={isExtensionDisabled}
                          />
                        </Col>
                        <Col flex="auto" style={{ paddingLeft: 8 }}>
                          {isExtensionDisabled ? (
                            <Tooltip title={tooltipText}>
                              <span
                                style={{ cursor: "pointer", color: "#999" }}
                              >
                                {svc.name}
                              </span>
                            </Tooltip>
                          ) : (
                            <span>{svc.name}</span>
                          )}
                        </Col>
                        <Col>{svc.price} EUR</Col>
                      </Row>
                    );
                  })
              )}
              {form.combo && (
                <div
                  style={{
                    color: "#d33092",
                    fontSize: 12,
                    textAlign: "center",
                    marginTop: 8,
                  }}
                >
                  <CheckCircleFilled /> Pasirinkus spragėsius ir cukraus vatą
                  kartu taikoma -10 EUR nuolaida
                </div>
              )}
            </Space>
          </CardPartial>
        );
      }

      case 3: {
        const selectedTime = form.availableTimes.find(
          (t: any) => t.uuid === form.activeTime,
        );
        const selectedServices = form.services.filter((s: any) =>
          form.selectedServices.includes(s.uuid),
        );

        // Calculate adjusted time based on selected extension services
        const extensionMinutes = selectedServices
          .filter((s: any) => s.extension_minutes)
          .reduce((sum: number, s: any) => sum + s.extension_minutes, 0);

        const adjustTime = (
          timeStr: string,
          minutes: number,
          direction: "earlier" | "later",
        ): string => {
          if (!timeStr || minutes === 0) return timeStr;
          const parts = timeStr.split(":");
          const h = parseInt(parts[0]);
          const m = parseInt(parts[1]);
          const totalMinutes =
            direction === "earlier"
              ? h * 60 + m - minutes
              : h * 60 + m + minutes;
          const newH = Math.floor(totalMinutes / 60);
          const newM = totalMinutes % 60;
          return `${String(newH).padStart(2, "0")}:${String(newM).padStart(2, "0")}`;
        };

        const displayStartTime = selectedTime
          ? form.isFirstTimeSlotSelected && extensionMinutes > 0
            ? adjustTime(selectedTime.time_start, extensionMinutes, "earlier")
            : selectedTime.time_start.substring(
                0,
                selectedTime.time_start.length - 3,
              )
          : "";
        const displayEndTime = selectedTime
          ? !form.isFirstTimeSlotSelected && extensionMinutes > 0
            ? adjustTime(selectedTime.time_end, extensionMinutes, "later")
            : selectedTime.time_end.substring(
                0,
                selectedTime.time_end.length - 3,
              )
          : "";

        // Calculate jubiliato age
        let jubiliatoAge: number | null = null;
        if (form.childName && form.childBirthDate && form.selectedDate) {
          const birth = dayjs(form.childBirthDate);
          const party = dayjs(form.selectedDate);
          if (birth.isValid() && party.isValid()) {
            const birthdayThisYear = birth.year(party.year());
            const birthdayNextYear = birth.year(party.year() + 1);
            const birthdayLastYear = birth.year(party.year() - 1);
            const diffThis = Math.abs(party.diff(birthdayThisYear, "day"));
            const diffNext = Math.abs(party.diff(birthdayNextYear, "day"));
            const diffLast = Math.abs(party.diff(birthdayLastYear, "day"));
            if (diffThis <= diffLast && diffThis <= diffNext) {
              jubiliatoAge = party.year() - birth.year();
            } else if (diffLast <= diffNext) {
              jubiliatoAge = party.year() - 1 - birth.year();
            } else {
              jubiliatoAge = party.year() + 1 - birth.year();
            }
            if (jubiliatoAge <= 0) jubiliatoAge = null;
          }
        }

        return (
          <>
            <CardPartial
              isPosting={form.isPosting}
              title="Užsakymo santrauka"
              active_time={form.activeTime}
            >
              <div
                style={{ display: "flex", flexDirection: "column", gap: "4px" }}
              >
                <div>
                  <strong>Miestas:</strong> {form.selectedCity?.name}
                </div>
                <div>
                  <strong>Data:</strong> {form.selectedDate}
                </div>
                {selectedTime && (
                  <div>
                    <strong>Laikas:</strong> {displayStartTime}-{displayEndTime}
                    {extensionMinutes > 0 && (
                      <span
                        style={{
                          color: "#d33092",
                          fontSize: "12px",
                          marginLeft: "4px",
                        }}
                      >
                        (
                        {form.isFirstTimeSlotSelected
                          ? "paankstinta"
                          : "pratęsta"}{" "}
                        {extensionMinutes} min.)
                      </span>
                    )}
                  </div>
                )}
                <div>
                  <strong>Organizatorius:</strong> {form.parentName}
                </div>
                {form.childName && (
                  <div>
                    <strong>Jubiliatas:</strong> {form.childName}
                  </div>
                )}
                {jubiliatoAge && (
                  <div>
                    <strong>Amžius:</strong> {jubiliatoAge}
                  </div>
                )}
                <div>
                  <strong>Svečių skaičius:</strong> {form.guestCount}
                </div>
                <div>
                  <strong>Telefonas:</strong> +370{form.phone}
                </div>
                <div>
                  <strong>El. paštas:</strong> {form.customerEmail}
                </div>
                <div>
                  <strong>Adresas:</strong> {form.address}
                </div>
              </div>

              {selectedServices.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <strong>Papildukai:</strong>
                  <ul style={{ marginTop: "4px", marginBottom: "8px" }}>
                    {selectedServices.map((svc: any) => (
                      <li key={svc.uuid}>
                        {svc.name} - {svc.price}€
                      </li>
                    ))}
                  </ul>
                  {form.combo && (
                    <div style={{ color: "#d33092", fontSize: 12 }}>
                      <CheckCircleFilled /> Pasirinkus spragėsius ir cukraus
                      vatą kartu taikoma -10 EUR nuolaida
                    </div>
                  )}
                </div>
              )}
            </CardPartial>

            <CardPartial
              isPosting={form.isPosting}
              title="Nuolaidos kodas"
              active_time={form.activeTime}
            >
              <Input
                value={form.discountCode}
                onChange={(e) => form.setDiscountCode(e.target.value)}
                placeholder="Nuolaidos kodas"
                style={{ width: "100%" }}
              />
              <div style={{ textAlign: "center", width: "100%" }}>
                <div style={{ marginTop: 8 }}>
                  {form.discountStatus === true && (
                    <span style={{ color: "green" }}>
                      <CheckCircleFilled /> Nuolaidos kodas pritaikytas
                    </span>
                  )}
                  {form.discountStatus === false && (
                    <span style={{ color: "red" }}>
                      <CloseCircleFilled /> Nuolaidos kodas nerastas
                    </span>
                  )}
                </div>
                <Button
                  onClick={form.checkDiscount}
                  type="primary"
                  style={{ marginTop: 8 }}
                  disabled={!form.selectedDate}
                >
                  Patikrinti
                </Button>
              </div>
            </CardPartial>

            <CardPartial
              isPosting={form.isPosting}
              title="Mokėtina suma"
              active_time={form.activeTime}
            >
              <div
                style={{
                  width: "100%",
                  fontSize: 48,
                  textAlign: "center",
                  color: "#D33092",
                }}
              >
                {form.price}
                <sup>
                  <small>EUR</small>
                </sup>
              </div>

              <div style={{ textAlign: "center", width: "100%" }}>
                <div
                  style={{
                    marginBottom: 16,
                    textAlign: "left",
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  <Checkbox
                    checked={form.marketingConsent}
                    onChange={(e) => form.setMarketingConsent(e.target.checked)}
                  >
                    <span>
                      {formConfig.gdpr_consent_text ||
                        "Sutinku gauti rinkodaros pranešimus ir pasiūlymus"}
                    </span>
                  </Checkbox>
                  <Checkbox
                    checked={form.confirmationChecked}
                    onChange={(e) => {
                      form.setConfirmationChecked(e.target.checked);
                      form.setDataProcessingConsent(e.target.checked);
                    }}
                  >
                    Patvirtinu, kad visi duomenys yra teisingi, sutinku su{" "}
                    <a
                      href="https://pramoguloftas.lt/privatumo-politika/"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#66469C", textDecoration: "underline" }}
                    >
                      privatumo politika
                    </a>{" "}
                    bei paslaugų teikimo sąlygomis *
                  </Checkbox>
                </div>
              </div>
            </CardPartial>
          </>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div style={{ maxWidth: "400px", fontFamily: "Poppins, sans-serif" }}>
      <StepIndicator
        currentStep={form.currentStep}
        totalSteps={totalSteps}
        labels={stepLabels}
        onStepClick={(step) => {
          if (step <= form.currentStep) form.setCurrentStep(step);
        }}
      />

      {renderStep()}

      {form.currentStep < totalSteps - 1 ? (
        <Card style={{ marginTop: "16px", textAlign: "center" }}>
          {form.currentStep > 0 && (
            <Button onClick={form.prevStep} style={{ marginRight: "8px" }}>
              Atgal
            </Button>
          )}
          <Button
            type="primary"
            onClick={form.nextStep}
            disabled={!canGoNext()}
          >
            Kitas žingsnis
          </Button>
        </Card>
      ) : (
        <Card style={{ marginTop: "16px", textAlign: "center" }}>
          <Button onClick={form.prevStep} style={{ marginRight: "8px" }}>
            Atgal
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={form.isPosting}
            disabled={!canGoNext()}
          >
            Pateikti užsakymą
          </Button>
        </Card>
      )}
    </div>
  );
};

export default PrivatePartyForm;
